import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getPartner, servicePricing } from "../../../../Service/partnerAPI";
import Loader from "../../../../reusableUI/loader";
import { InputNumber, message, Table, Select, Input, AutoComplete, Popover } from "antd";
import { Plus, X, Pencil, Check, Ban, Info } from "lucide-react";
import ToggleTabs from "../../../../reusableUI/tap";
import secureLocalStorage from "react-secure-storage";
import { getLookupData } from "../../../../Service/lookup";
import { capitalizeFirstLetter } from "../../../../utils/Functions";

const ONBOARDING_FIRST_KEY = "pricingOnboardingFirst";
const MAX_FEE = 99999;
const PHYSIO_WRAPPER_KEY = "physioService";
const ASSUME_DB_RETURNS_NET_FEE = true;

const ALL_SERVICE_TABS = [
  { label: "Nurse", value: "Nurse" },
  { label: "Physiotherapist", value: "Physiotherapist" },
];

const SUPPORTED_SECTIONS = new Set(["Nurse", "Physiotherapist"]);

const ONE_TIME_EXCLUDE_SERVICES = [
  "Tracheostomy",
  "Continuous Ryle's Tube Aspiration",
  "Hourly Ryle's Tube Aspiration",
];

const NURSE_ROLES = [
  { label: "Nurse", value: "nurse" },
  { label: "Nursing Assistant", value: "nursing-assistant" },
];


const SHIFT_DURATION_OPTIONS = [
  { label: "Morning 9H", value: "Morning|9H", shiftType: "Morning", duration: "9H" },
  { label: "Morning 12H", value: "Morning|12H", shiftType: "Morning", duration: "12H" },
  { label: "Night 9H", value: "Night|9H", shiftType: "Night", duration: "9H" },
  { label: "Night 12H", value: "Night|12H", shiftType: "Night", duration: "12H" },
  { label: "24H", value: "24H|24H", shiftType: "24H", duration: "24H" },
];

const labelCls = "text-xs font-semibold text-gray-600 mb-1 leading-tight block";
const fieldWrap = "flex flex-col min-w-0 w-full";

/* ---------------- Utils ---------------- */

const uuid = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const shiftFromShiftType = (shiftType) => {
  if (shiftType === "Morning") return "morning";
  if (shiftType === "Night") return "night";
  if (shiftType === "24H") return "24/7";
  return "";
};

const shiftTypeFromShift = (shift) => {
  if (shift === "morning") return "Morning";
  if (shift === "night") return "Night";
  if (shift === "24/7") return "24H";
  return shift || "";
};

const safeArr = (v) => (Array.isArray(v) ? v : []);
const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const roundCurrency = (value) => Number(toNumber(value).toFixed(2));
const pickFee = (obj) => toNumber(obj?.fee ?? obj?.fees ?? 0);

const clampInt = (v, { min = 0, max = MAX_FEE } = {}) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const i = Math.trunc(n);
  return Math.min(max, Math.max(min, i));
};

const normalizeDiscount = (discount) => {
  if (!discount) return null;
  if (typeof discount === "object" && discount?.type && discount?.value != null) {
    const t = discount.type === "fixed" ? "fixed" : "percentage";
    const v = Number(discount.value);
    if (!Number.isFinite(v) || v <= 0) return null;
    return { type: t, value: v };
  }
  if (typeof discount === "string") {
    const str = discount.trim();
    if (!str) return null;
    if (str.includes("%")) {
      const v = Number(str.replace("%", "").trim());
      if (!Number.isFinite(v) || v <= 0) return null;
      return { type: "percentage", value: v };
    }
    const numeric = Number(str.replace(/[^\d.]/g, ""));
    if (Number.isFinite(numeric) && numeric > 0) return { type: "fixed", value: numeric };
  }
  return null;
};

const computeFinalFee = (baseFee, discountObj) => {
  const fee = Number(baseFee || 0);
  if (!discountObj || !fee) return fee;
  const v = Number(discountObj.value || 0);
  if (!Number.isFinite(v) || v <= 0) return fee;
  let final = fee;
  if (discountObj.type === "percentage") {
    const pct = Math.min(Math.max(v, 0), 100);
    final = fee - (fee * pct) / 100;
  } else {
    final = fee - v;
  }
  return roundCurrency(Math.max(0, final));
};

const invertDiscount = (netFee, discountObj) => {
  const n = Number(netFee || 0);
  if (!discountObj || !n) return n;
  if (discountObj.type === "percentage") {
    const pct = Math.min(Math.max(Number(discountObj.value || 0), 0), 100);
    const factor = 1 - pct / 100;
    if (factor <= 0) return n;
    return roundCurrency(n / factor);
  }
  const v = Number(discountObj.value || 0);
  if (!Number.isFinite(v) || v < 0) return n;
  return roundCurrency(n + v);
};

const formatDiscount = (discountObj) => {
  if (!discountObj) return "--";
  const v = Number(discountObj.value || 0);
  if (!Number.isFinite(v) || v <= 0) return "--";
  return discountObj.type === "percentage" ? `${v}%` : `₹${v}`;
};

const normalizeServiceName = (v) => String(v || "").trim().replace(/\s+/g, " ");
const isSameService = (a, b) =>
  normalizeServiceName(a).toLowerCase() === normalizeServiceName(b).toLowerCase();
const isGeneralService = (name) => isSameService(name, "General Services");
const isExcludedOneTimeService = (name) =>
  ONE_TIME_EXCLUDE_SERVICES.some((x) => isSameService(name, x));

const normalizeRole = (v) => {
  const s = String(v || "").trim().toLowerCase();
  if (!s) return "";
  if (s === "nurse") return "Nurse";
  if (s === "nursing assistant" || s === "nursingassistant" || s === "assistant")
    return "Nursing Assistant";
  return v;
};

const uniqTrimmed = (arr) => {
  const out = [];
  const seen = new Set();
  for (const x of safeArr(arr)) {
    const v = normalizeServiceName(x);
    if (!v) continue;
    const k = v.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
};

const extractPartnerServices = (partnerData) => {
  const raw = safeArr(partnerData?.services);
  return raw
    .map((item) => {
      if (typeof item === "string") return item;
      return item?.name || item?.label || item?.serviceType || item?.title || "";
    })
    .map((x) => String(x || "").trim())
    .filter(Boolean);
};

const readOnboardingFirst = () => {
  const v = secureLocalStorage.getItem(ONBOARDING_FIRST_KEY);
  return v === true || v === "true" || v === "1" || v === 1;
};

const resolvePricingSource = (pricingData) => {
  if (!pricingData || typeof pricingData !== "object") return null;
  if (pricingData?.pricingInfo && typeof pricingData.pricingInfo === "object")
    return pricingData.pricingInfo;
  return pricingData;
};

const hasAnyPricing = (src) => {
  if (!src) return false;
  const nurse = src?.nurseService;
  const physio = src?.physioService || src?.physioServices || src?.[PHYSIO_WRAPPER_KEY];
  const nurseRegular = safeArr(nurse?.regularShifts?.slots).length;
  const nurseOneTime = safeArr(nurse?.oneTimeShifts?.services).length;
  const physioServices = safeArr(physio?.services).length;
  const physioAssessment = toNumber(physio?.assessmentFees ?? physio?.assessmentFee ?? 0);
  return nurseRegular > 0 || nurseOneTime > 0 || physioServices > 0 || physioAssessment > 0;
};

const buildTabsFromPricing = (src) => {
  const tabs = [];
  if (!src) return ALL_SERVICE_TABS;
  const nurse = src?.nurseService;
  const physio = src?.physioService || src?.physioServices || src?.[PHYSIO_WRAPPER_KEY];
  const doctor = src?.doctorService;

  const nurseHas =
    safeArr(nurse?.regularShifts?.slots).length > 0 ||
    safeArr(nurse?.oneTimeShifts?.services).length > 0;

  const physioHas =
    safeArr(physio?.services).length > 0 ||
    toNumber(physio?.assessmentFees ?? physio?.assessmentFee ?? 0) > 0;

  const doctorHas = safeArr(doctor?.services).length > 0;

  if (nurseHas) tabs.push({ label: "Nurse", value: "Nurse" });
  if (physioHas) tabs.push({ label: "Physiotherapist", value: "Physiotherapist" });
  if (doctorHas) tabs.push({ label: "Doctor", value: "Doctor" });

  return tabs.length ? tabs : ALL_SERVICE_TABS;
};

const makeIdentity = (row) => {
  const kind = row?.kind;
  if (kind === "regular") {
    const role = normalizeRole(row.role);
    const serviceName = normalizeServiceName(row.serviceName || row.service);
    return `regular|${role}|${serviceName.toLowerCase()}|${row.shiftType || ""}|${row.duration || ""}`;
  }
  if (kind === "onetime") return `onetime|${normalizeServiceName(row.service).toLowerCase()}`;
  if (kind === "physio" || kind === "physio_assessment")
    return `physio|${normalizeServiceName(row.service).toLowerCase()}`;
  return `other|${normalizeServiceName(row.service).toLowerCase()}`;
};

const identityOf = (row) => row?.identity || makeIdentity(row);

const normalizeRowFromLocal = (row) => {
  const baseFee = pickFee(row);
  const d = normalizeDiscount(row.discount);
  const out = { ...row, fee: baseFee, discount: d, finalFee: computeFinalFee(baseFee, d) };
  const identity = row?.identity || makeIdentity(out);
  return { ...out, identity };
};

const normalizeRowFromDB = (row) => {
  const raw = pickFee(row);
  const d = normalizeDiscount(row.discount);
  let baseFee = raw;
  if (ASSUME_DB_RETURNS_NET_FEE && d) {
    baseFee = invertDiscount(raw, d);
  }
  const out = { ...row, fee: baseFee, discount: d, finalFee: computeFinalFee(baseFee, d) };
  const identity = row?.identity || makeIdentity(out);
  return { ...out, identity };
};

const applyDraft = (savedList, draftList) => {
  const map = new Map(savedList.map((r) => [identityOf(r), r]));
  for (const d of draftList) {
    const id = identityOf(d);
    if (d?._change === "delete") map.delete(id);
    else map.set(id, d);
  }
  return Array.from(map.values()).filter((r) => r?._change !== "delete");
};

/* ---------------- Helper: encode / decode combined shift-duration value ---------------- */
const encodeShiftDuration = (shiftType, duration) => {
  if (!shiftType) return undefined;
  return `${shiftType}|${duration || shiftType}`;
};

const decodeShiftDuration = (combined) => {
  if (!combined) return { shiftType: "", duration: "" };
  const opt = SHIFT_DURATION_OPTIONS.find((o) => o.value === combined);
  if (opt) return { shiftType: opt.shiftType, duration: opt.duration };
  // fallback: split on pipe
  const [shiftType, duration] = combined.split("|");
  return { shiftType: shiftType || "", duration: duration || shiftType || "" };
};

/* ---------------- Component ---------------- */

const Pricing = ({ pricingData, readOnly: readOnlyProp = undefined }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const pricingSource = useMemo(() => resolvePricingSource(pricingData), [pricingData]);
  const pricingProvided = useMemo(() => hasAnyPricing(pricingSource), [pricingSource]);
  const readOnly = readOnlyProp ?? pricingProvided;

  const firstFromState = location.state?.first;

  const [onboardingFirst, setOnboardingFirst] = useState(() => {
    if (typeof firstFromState === "boolean") return firstFromState;
    return readOnboardingFirst();
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasPricingInDB, setHasPricingInDB] = useState(false);

  const [serviceList, setServiceList] = useState([]);
  const [activeSection, setActiveSection] = useState("Nurse");
  const [nurseMode, setNurseMode] = useState("onetime");

  const [savedNurseRegular, setSavedNurseRegular] = useState([]);
  const [savedNurseOneTime, setSavedNurseOneTime] = useState([]);
  const [savedPhysio, setSavedPhysio] = useState([]);
  const [savedDoctor, setSavedDoctor] = useState([]);

  const [draftNurseRegular, setDraftNurseRegular] = useState([]);
  const [draftNurseOneTime, setDraftNurseOneTime] = useState([]);
  const [draftPhysio, setDraftPhysio] = useState([]);
  const [draftDoctor, setDraftDoctor] = useState([]);

  const [nurseIndividualServices, setNurseIndividualServices] = useState([]);
  const [nurseGeneralServiceInfo, setNurseGeneralServiceInfo] = useState([]);
  const [physioServices, setPhysioServices] = useState([]);
  const [formShiftDuration, setFormShiftDuration] = useState("");

  const [formShiftType, setFormShiftType] = useState("");
  const [formDuration, setFormDuration] = useState("");
  const [formService, setFormService] = useState("");
  const [formFee, setFormFee] = useState(null);
  const [formServiceName, setFormServiceName] = useState("");
  const [formRole, setFormRole] = useState("");
  const [formServices, setFormServices] = useState([]);
  const [formDiscountType, setFormDiscountType] = useState("percentage");
  const [formDiscountValue, setFormDiscountValue] = useState(null);
  const [formNotes, setFormNotes] = useState("");

  const [editingKey, setEditingKey] = useState(null);
  const [editingIdentity, setEditingIdentity] = useState(null);

  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--app-vh", `${vh}px`);
    };
    setVH();
    window.addEventListener("resize", setVH);
    return () => window.removeEventListener("resize", setVH);
  }, []);

  const isNurse = activeSection === "Nurse";
  const isRegular = isNurse && nurseMode === "regular";
  const isSupportedSection = readOnly ? true : SUPPORTED_SECTIONS.has(activeSection);

  useEffect(() => {
    if (typeof firstFromState === "boolean") {
      setOnboardingFirst(firstFromState);
      secureLocalStorage.setItem(ONBOARDING_FIRST_KEY, firstFromState ? "1" : "0");
    }
  }, [firstFromState]);

  const resetForm = () => {
    setFormShiftDuration("");
    setFormShiftType("");
    setFormDuration("");
    setFormService("");
    setFormFee(null);
    setFormServiceName("");
    setFormRole("");
    setFormServices([]);
    setFormDiscountType("percentage");
    setFormDiscountValue(null);
    setFormNotes("");
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditingIdentity(null);
    resetForm();
  };

  const getServices = async () => {
    try {
      const res = await getLookupData();
      if (res?.error !== false || !Array.isArray(res?.data)) return;
      const rows = res.data.filter((x) => String(x?.type || "").trim() === "Services");

      const pickLabel = (x) => String(x?.label || x?.name || x?.value || "").trim();
      const uniqueOrdered = (arr) => {
        const out = [];
        const seen = new Set();
        for (const s of arr) {
          const v = String(s || "").trim();
          const key = v.toLowerCase();
          if (!v || seen.has(key)) continue;
          seen.add(key);
          out.push(v);
        }
        return out;
      };

      const nurseGeneral = uniqueOrdered(
        rows
          .filter((x) => String(x?.role || "").toLowerCase().trim() === "nurse")
          .filter((x) => String(x?.serviceType || "").toLowerCase().trim() === "general")
          .map(pickLabel)
          .filter(Boolean)
      );

      const nurseIndividual = uniqueOrdered(
        rows
          .filter((x) => String(x?.role || "").toLowerCase().trim() === "nurse")
          // .filter((x) => String(x?.serviceType || "").toLowerCase().trim() !== "general")
          .map(pickLabel)
          .filter(Boolean)
      );

      const physio = uniqueOrdered(
        rows
          .filter((x) => {
            const role = String(x?.role || "").toLowerCase().trim();
            return role === "physiotherapist" || role === "physio";
          })
          .map(pickLabel)
          .filter(Boolean)
      );

      setNurseGeneralServiceInfo(nurseGeneral);
      setNurseIndividualServices(nurseIndividual);
      setPhysioServices(physio);
    } catch (e) {
      console.error("lookup error", e);
    }
  };

  const hydrateFromPricingData = () => {
    const src = pricingSource;
    if (!src) {
      setSavedNurseRegular([]);
      setSavedNurseOneTime([]);
      setSavedPhysio([]);
      setSavedDoctor([]);
      setHasPricingInDB(false);
      return;
    }

    const tabs = buildTabsFromPricing(src);
    if (tabs.length === 1) setActiveSection(tabs[0].value);
    else if (!tabs.some((t) => t.value === activeSection))
      setActiveSection(tabs[0]?.value || "Nurse");

    const nurseNode = src?.nurseService || {};
    const regularSlots = safeArr(nurseNode?.regularShifts?.slots);
    const oneTimeServices = safeArr(nurseNode?.oneTimeShifts?.services);

    const regularTable = regularSlots
      .map((slot) => {
        const shiftType = shiftTypeFromShift(slot?.shift);
        const duration = String(slot?.type || "").trim();
        const fallbackName = `${duration || ""} Duty - ${shiftType}`.trim();
        const serviceName = normalizeServiceName(
          slot?.serviceName || slot?.name || slot?.service || fallbackName
        );
        const role = normalizeRole(slot?.role || slot?.staffRole || "Nurse");
        const services = uniqTrimmed(slot?.services);

        return normalizeRowFromDB({
          key: uuid(),
          kind: "regular",
          shiftType,
          duration: duration || "-",
          serviceName,
          role,
          services,
          fee: pickFee(slot),
          notes: slot?.notes || "",
          discount: slot?.discount || null,
          shift: slot?.shift || "",
          type: slot?.type || "",
        });
      })
      .filter((x) => x.shiftType && x.fee > 0);

    const oneTimeTable = oneTimeServices
      .map((srv) =>
        normalizeRowFromDB({
          key: uuid(),
          kind: "onetime",
          shiftType: "One Time",
          duration: "-",
          service: srv?.serviceType || srv?.name || srv?.label || "",
          fee: pickFee(srv),
          notes: srv?.notes || "",
          discount: srv?.discount || null,
          serviceType: srv?.serviceType || srv?.name || srv?.label || "",
          services: safeArr(srv?.services),
        })
      )
      .filter((x) => x.service && x.fee > 0);

    setSavedNurseRegular(regularTable);
    setSavedNurseOneTime(oneTimeTable);

    if (activeSection === "Nurse") {
      if (oneTimeTable.length) setNurseMode("onetime");
      else if (regularTable.length) setNurseMode("regular");
    }

    const physioNode =
      src?.physioService || src?.physioServices || src?.[PHYSIO_WRAPPER_KEY] || {};
    const assessmentFeesNum = toNumber(
      physioNode?.assessmentFees ?? physioNode?.assessmentFee ?? 0
    );
    const physioSrv = safeArr(physioNode?.services);

    const treatments = physioSrv
      .map((x) =>
        normalizeRowFromDB({
          key: uuid(),
          kind: "physio",
          shiftType: "Treatment",
          duration: "-",
          service: x?.name || x?.serviceType || x?.label || "",
          fee: pickFee(x),
          discount: x?.discount || null,
          notes: x?.notes || "",
          _id: x?._id,
        })
      )
      .filter((x) => x.service && x.fee > 0);

    const physioTable = [];
    if (assessmentFeesNum > 0) {
      physioTable.push(
        normalizeRowFromLocal({
          key: uuid(),
          kind: "physio_assessment",
          shiftType: "Visit",
          duration: "-",
          service: "Assessment",
          fee: assessmentFeesNum,
          discount: null,
          notes: "",
        })
      );
    }
    physioTable.push(...treatments);

    setSavedPhysio(physioTable);
    setSavedDoctor([]);
    setHasPricingInDB(hasAnyPricing(src));

    setDraftNurseRegular([]);
    setDraftNurseOneTime([]);
    setDraftPhysio([]);
    setDraftDoctor([]);
  };

  const fetchPartnerData = async () => {
    try {
      const res = await getPartner();
      if (res?.error !== false) {
        message.error(res?.message || "Failed to load pricing data");
        return;
      }
      const data = res?.data || {};
      setServiceList(extractPartnerServices(data));

      const nurseNode = data?.pricingInfo?.nurseService || {};
      const regularShifts = safeArr(nurseNode?.regularShifts?.slots);
      const oneTimeShifts = safeArr(nurseNode?.oneTimeShifts?.services);

      const regularTableData = regularShifts
        .map((slot) => {
          const shiftType = shiftTypeFromShift(slot?.shift);
          const duration = String(slot?.type || "").trim();
          const fallbackName = `${duration || ""} Duty - ${shiftType}`.trim();
          const serviceName = normalizeServiceName(
            slot?.serviceName || slot?.name || slot?.service || fallbackName
          );
          const role = normalizeRole(slot?.role || slot?.staffRole || "Nurse");
          const services = uniqTrimmed(slot?.services);

          return normalizeRowFromDB({
            key: uuid(),
            kind: "regular",
            shiftType,
            duration,
            serviceName,
            role,
            services,
            fee: pickFee(slot),
            notes: slot?.notes || "",
            discount: slot?.discount || null,
            shift: slot?.shift || "",
            type: slot?.type || "",
          });
        })
        .filter((row) => row.shiftType && row.fee > 0);

      const oneTimeTableData = oneTimeShifts
        .map((srv) =>
          normalizeRowFromDB({
            key: uuid(),
            kind: "onetime",
            shiftType: "One Time",
            duration: "-",
            service: srv?.serviceType || srv?.name || srv?.label || "",
            fee: pickFee(srv),
            notes: srv?.notes || "",
            discount: srv?.discount || null,
            serviceType: srv?.serviceType || srv?.name || srv?.label || "",
            services: safeArr(srv?.services),
          })
        )
        .filter((row) => row.service && row.fee > 0);

      setSavedNurseRegular(regularTableData);
      setSavedNurseOneTime(oneTimeTableData);

      const physioNode =
        data?.pricingInfo?.physioService ||
        data?.pricingInfo?.physioServices ||
        data?.pricingInfo?.[PHYSIO_WRAPPER_KEY] ||
        {};
      const assessmentFeesNum = toNumber(
        physioNode?.assessmentFees ?? physioNode?.assessmentFee ?? 0
      );

      const physioSrv = safeArr(physioNode?.services);
      const treatments = physioSrv
        .map((x) =>
          normalizeRowFromDB({
            key: uuid(),
            kind: "physio",
            shiftType: "Treatment",
            duration: "-",
            service: x?.name || x?.serviceType || x?.label || "",
            fee: pickFee(x),
            discount: x?.discount || null,
            notes: x?.notes || "",
            _id: x?._id,
          })
        )
        .filter((x) => x.service && x.fee > 0);

      const physioTable = [];
      if (assessmentFeesNum > 0) {
        physioTable.push(
          normalizeRowFromLocal({
            key: uuid(),
            kind: "physio_assessment",
            shiftType: "Visit",
            duration: "-",
            service: "Assessment",
            fee: assessmentFeesNum,
            discount: null,
            notes: "",
          })
        );
      }
      physioTable.push(...treatments);
      setSavedPhysio(physioTable);
      setSavedDoctor([]);

      const pricingExists =
        regularTableData.length > 0 ||
        oneTimeTableData.length > 0 ||
        treatments.length > 0 ||
        assessmentFeesNum > 0;

      setHasPricingInDB(pricingExists);

      setDraftNurseRegular([]);
      setDraftNurseOneTime([]);
      setDraftPhysio([]);
      setDraftDoctor([]);
    } catch (e) {
      console.error("fetchPartnerData error", e);
      message.error("Failed to load pricing data");
    }
  };

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      try {
        if (readOnly) {
          hydrateFromPricingData();
        } else {
          await Promise.all([fetchPartnerData(), getServices()]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [readOnly, pricingSource]);

  useEffect(() => {
    cancelEdit();
  }, [activeSection]);

  const serviceTabs = useMemo(() => {
    if (readOnly) return buildTabsFromPricing(pricingSource);
    if (!Array.isArray(serviceList) || serviceList.length === 0) return ALL_SERVICE_TABS;

    const set = new Set(serviceList.map((s) => String(s || "").toLowerCase().trim()));
    const hasNurse =
      set.has("nurse") || set.has("nursing assistant") || set.has("nursingassistant");
    const hasPhysio =
      set.has("physiotherapist") || set.has("physio") || set.has("physiotherapy");

    const tabs = [];
    if (hasNurse) tabs.push({ label: "Nurse", value: "Nurse" });
    if (hasPhysio) tabs.push({ label: "Physiotherapist", value: "Physiotherapist" });
    return tabs.length ? tabs : ALL_SERVICE_TABS;
  }, [readOnly, pricingSource, serviceList]);

  useEffect(() => {
    if (serviceTabs.length === 1) {
      setActiveSection(serviceTabs[0].value);
      return;
    }
    if (!serviceTabs.some((t) => t.value === activeSection)) {
      setActiveSection(serviceTabs[0]?.value || "Nurse");
    }
  }, [serviceTabs]); // eslint-disable-line react-hooks/exhaustive-deps

  const savedActiveData = useMemo(() => {
    if (activeSection === "Nurse")
      return nurseMode === "regular" ? savedNurseRegular : savedNurseOneTime;
    if (activeSection === "Physiotherapist") return savedPhysio;
    return savedDoctor;
  }, [activeSection, nurseMode, savedNurseRegular, savedNurseOneTime, savedPhysio, savedDoctor]);

  const draftActiveData = useMemo(() => {
    if (activeSection === "Nurse")
      return nurseMode === "regular" ? draftNurseRegular : draftNurseOneTime;
    if (activeSection === "Physiotherapist") return draftPhysio;
    return draftDoctor;
  }, [activeSection, nurseMode, draftNurseRegular, draftNurseOneTime, draftPhysio, draftDoctor]);

  const hasDraftChanges = draftActiveData.length > 0;
  const canProceed = useMemo(
    () => Boolean(onboardingFirst && hasPricingInDB),
    [onboardingFirst, hasPricingInDB]
  );

  const validateDiscount = (baseFee) => {
    const fee = Number(baseFee || 0);
    const hasDisc =
      formDiscountValue != null && formDiscountValue !== "" && Number(formDiscountValue) > 0;
    if (!hasDisc) return true;

    const v = Number(formDiscountValue);
    if (formDiscountType === "percentage") {
      if (!Number.isFinite(v) || v <= 0) return true;
      if (!Number.isInteger(v)) {
        message.warning("Percentage discount must be a whole number (no decimals)");
        return false;
      }
      if (v >= 100) {
        message.warning("Percentage discount must be below 100");
        return false;
      }
      return true;
    }

    if (!Number.isFinite(v) || v <= 0) return true;
    if (!Number.isInteger(v)) {
      message.warning("Fixed discount must be a whole number (no decimals)");
      return false;
    }
    if (fee > 0 && v >= fee) {
      message.warning("Fixed discount must be less than Fee (not equal / not greater)");
      return false;
    }
    return true;
  };

  const getDiscountObjFromForm = () => {
    const v = Number(formDiscountValue);
    if (!Number.isFinite(v) || v <= 0) return null;
    if (formDiscountType === "percentage") return { type: "percentage", value: Math.trunc(v) };
    return { type: "fixed", value: Math.trunc(v) };
  };

  const generalInfoContent = useMemo(() => {
    const list = safeArr(nurseGeneralServiceInfo);
    if (!list.length)
      return <div className="text-xs text-gray-500">No general service details found.</div>;
    return (
      <div style={{ maxWidth: 320 }}>
        <div className="text-xs font-semibold text-gray-800 mb-2">
          Included in General Services
        </div>
        <ul className="list-disc pl-4 text-xs text-gray-700 space-y-1">
          {list.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      </div>
    );
  }, [nurseGeneralServiceInfo]);

  const renderDiscountField = () => {
    const fee = Number(formFee || 0);
    const maxPercent = 99;
    const maxFixed = fee > 0 ? Math.max(0, Math.trunc(fee) - 1) : MAX_FEE;

    return (
      <div className="w-full">
        <label className={labelCls}>Discount</label>
        <div className="flex items-center gap-2 min-w-0">
          <InputNumber
            placeholder="0"
            value={formDiscountValue}
            onChange={(v) => {
              if (formDiscountType === "percentage")
                setFormDiscountValue(clampInt(v, { min: 0, max: maxPercent }));
              else setFormDiscountValue(clampInt(v, { min: 0, max: maxFixed }));
            }}
            min={0}
            max={formDiscountType === "percentage" ? maxPercent : maxFixed}
            precision={0}
            step={1}
            controls={false}
            size="small"
            className="flex-1 min-w-0 p-2"
            style={{ borderRadius: 8, height: 38 }}
            parser={(val) => String(val ?? "").replace(/[^\d]/g, "")}
            formatter={(val) => String(val ?? "").replace(/[^\d]/g, "")}
          />
          <Select
            value={formDiscountType}
            onChange={(t) => {
              setFormDiscountType(t);
              setFormDiscountValue(null);
            }}
            size="small"
            style={{ width: 70, borderRadius: 8, height: 38 }}
            dropdownMatchSelectWidth={false}
            showSearch={false}
          >
            <Select.Option value="percentage">%</Select.Option>
            <Select.Option value="fixed">₹</Select.Option>
          </Select>
        </div>
      </div>
    );
  };

  const renderServiceLabelWithInfo = () => {
    const showInfo =
      isNurse &&
      nurseMode === "onetime" &&
      isGeneralService(formService) &&
      nurseGeneralServiceInfo.length > 0;
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-semibold text-gray-600 leading-tight">Service</span>
        {showInfo && (
          <Popover content={generalInfoContent} trigger="click" placement="right">
            <button
              type="button"
              className="flex items-center justify-center w-5 h-5 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 font-poppins"
            >
              <Info className="w-3 h-3" />
            </button>
          </Popover>
        )}
      </div>
    );
  };

  const renderServiceInput = () => {
    if (readOnly) return null;

    if (activeSection === "Nurse" && nurseMode === "onetime") {
      const oneTimeOptions = [
        // { value: "General Services", label: "General Services" },
        ...nurseIndividualServices
          .filter((s) => !isExcludedOneTimeService(s))
          .map((s) => ({ value: s, label: s })),
      ];
      const disableServiceWhenEditing =
        Boolean(editingKey) && activeSection === "Nurse" && nurseMode === "onetime";

      return (
        <AutoComplete
          className="w-full mb-2 font-poppins"
          options={oneTimeOptions}
          value={formService || ""}
          onChange={setFormService}
          placeholder="Select or type service"
          disabled={disableServiceWhenEditing}
          filterOption={(input, option) =>
            (option?.label ?? option?.value ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          <Input
            size="small"
            disabled={disableServiceWhenEditing}
            style={{ borderRadius: 8, height: 38 }}
          />
        </AutoComplete>
      );
    }

    if (activeSection === "Physiotherapist") {
      const options = physioServices.map((s) => ({ value: s, label: s }));
      const disableServiceWhenEditing = Boolean(editingKey);
      return (
        <AutoComplete
          className="w-full"
          options={options}
          value={formService || ""}
          onChange={setFormService}
          placeholder="Select or type service"
          disabled={disableServiceWhenEditing}
          filterOption={(input, option) =>
            (option?.label ?? option?.value ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          <Input
            size="small"
            disabled={disableServiceWhenEditing}
            style={{ borderRadius: 8, height: 38 }}
          />
        </AutoComplete>
      );
    }

    return null;
  };

  const renderActionButtons = () => (
    <div className="flex items-center justify-center gap-2">
      {editingKey && (
        <button
          onClick={cancelEdit}
          className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
          type="button"
        >
          <Ban className="w-4 h-4" />
        </button>
      )}
      <button
        onClick={handleAddOrUpdate}
        className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
        type="button"
      >
        {editingKey ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
      </button>
    </div>
  );

  const upsertDraftRow = (setter, row) => {
    setter((prev) => {
      const id = identityOf(row);
      const filtered = prev.filter((r) => identityOf(r) !== id);
      return [...filtered, row];
    });
  };

  const startEditRow = (record) => {
    if (readOnly || !isSupportedSection) return;
    if (record?._change === "delete") return;

    setEditingKey(record.key);
    setEditingIdentity(identityOf(record));

    if (activeSection === "Nurse")
      setNurseMode(record.kind === "regular" ? "regular" : "onetime");

    if (record.kind === "regular") {
      setFormServiceName(record.serviceName || "");
      setFormRole(normalizeRole(record.role || "Nurse") || "Nurse");
      setFormServices(uniqTrimmed(record.services));

      // Restore combined shift+duration
      const combined = encodeShiftDuration(record.shiftType, record.duration);
      setFormShiftDuration(combined || "");
      setFormShiftType(record.shiftType || "");
      setFormDuration(record.duration || "");
    } else {
      setFormShiftDuration("");
      setFormShiftType(record.shiftType === "One Time" ? "" : record.shiftType || "");
      setFormDuration(record.duration === "-" ? "" : record.duration || "");
      setFormService(record.service || "");
    }

    setFormFee(clampInt(record.fee, { min: 0, max: MAX_FEE }));
    setFormNotes(record.notes || "");

    const d = normalizeDiscount(record.discount);
    setFormDiscountType(d?.type || "percentage");
    setFormDiscountValue(
      d?.value != null ? clampInt(d.value, { min: 0, max: MAX_FEE }) : null
    );
  };

  const queueDeleteSaved = (record) => {
    if (readOnly || !isSupportedSection) return;
    const del = normalizeRowFromLocal({
      ...record,
      key: uuid(),
      identity: identityOf(record),
      _change: "delete",
    });

    if (activeSection === "Nurse") {
      if (record.kind === "regular") upsertDraftRow(setDraftNurseRegular, del);
      else upsertDraftRow(setDraftNurseOneTime, del);
      return;
    }
    if (activeSection === "Physiotherapist") upsertDraftRow(setDraftPhysio, del);
  };

  const removeDraftRow = (record) => {
    if (readOnly || !isSupportedSection) return;
    if (editingKey === record.key) cancelEdit();

    if (activeSection === "Nurse") {
      if (record.kind === "regular")
        setDraftNurseRegular((p) => p.filter((x) => x.key !== record.key));
      else setDraftNurseOneTime((p) => p.filter((x) => x.key !== record.key));
      return;
    }
    if (activeSection === "Physiotherapist")
      setDraftPhysio((p) => p.filter((x) => x.key !== record.key));
  };

  const checkOneTimeDuplicate = (newService) => {
    const targetNorm = normalizeServiceName(newService).toLowerCase();
    const savedExist = savedNurseOneTime.some(
      (item) => normalizeServiceName(item.service).toLowerCase() === targetNorm
    );
    if (savedExist)
      return { valid: false, msg: "This One-Time Service already exists in Saved Pricing." };

    const editedId = editingKey;
    const draftExist = draftNurseOneTime.some(
      (item) =>
        item.key !== editedId &&
        normalizeServiceName(item.service).toLowerCase() === targetNorm
    );
    if (draftExist)
      return {
        valid: false,
        msg: "You already have a pending change for this One-Time Service.",
      };

    return { valid: true };
  };

  const checkRegularDuplicate = (newRow) => {
    const targetNorm = normalizeServiceName(newRow.serviceName).toLowerCase();
    const targetRole = normalizeRole(newRow.role).toLowerCase();
    const targetShift = newRow.shiftType.toLowerCase();
    const targetDuration = newRow.duration.toLowerCase();

    const isDuplicate = (item) => {
      const iName = normalizeServiceName(item.serviceName).toLowerCase();
      const iRole = normalizeRole(item.role).toLowerCase();
      const iShift = item.shiftType.toLowerCase();
      const iDuration = item.duration.toLowerCase();
      return (
        iName === targetNorm &&
        iRole === targetRole &&
        iShift === targetShift &&
        iDuration === targetDuration
      );
    };

    const savedExist = savedNurseRegular.some(isDuplicate);
    if (savedExist)
      return {
        valid: false,
        msg: "Shift combination (Service, Role, Shift, Duration) already exists.",
      };

    const editedId = editingKey;
    const draftExist = draftNurseRegular.some(
      (item) => item.key !== editedId && isDuplicate(item)
    );
    if (draftExist)
      return {
        valid: false,
        msg: "You already have a pending change for this Shift combination.",
      };

    return { valid: true };
  };

  const handleAddOrUpdate = () => {
    if (readOnly) return;
    if (!isSupportedSection) return message.info(`${activeSection} pricing is coming soon`);

    const feeInt = clampInt(formFee, { min: 0, max: MAX_FEE });
    if (feeInt == null || feeInt <= 0)
      return message.warning("Please enter valid Fee (max 5 digits)");
    if (!validateDiscount(feeInt)) return;

    const discountObj = getDiscountObjFromForm();
    const isEditing = Boolean(editingKey);

    if (activeSection === "Nurse") {
      if (nurseMode === "regular") {
        const serviceName = normalizeServiceName(formServiceName);
        const role = normalizeRole(formRole);

        if (!serviceName) return message.warning("Please enter Service Name");
        if (!role) return message.warning("Please select Role");

        // Validate combined shift+duration
        if (!formShiftDuration) return message.warning("Please select Shift & Duration");
        const { shiftType: resolvedShift, duration: resolvedDuration } =
          decodeShiftDuration(formShiftDuration);
        if (!resolvedShift) return message.warning("Please select Shift & Duration");

        const servicesFinal = uniqTrimmed(formServices);
        if (!servicesFinal.length)
          return message.warning("Please select/enter at least one Service");

        const dupCheck = checkRegularDuplicate({
          serviceName,
          role,
          shiftType: resolvedShift,
          duration: resolvedDuration,
        });
        if (!dupCheck.valid) return message.warning(dupCheck.msg);

        const row = normalizeRowFromLocal({
          key: isEditing ? editingKey : uuid(),
          kind: "regular",
          serviceName,
          role,
          services: servicesFinal,
          shiftType: resolvedShift,
          duration: resolvedDuration,
          fee: feeInt,
          discount: discountObj,
          notes: formNotes,
          shift: shiftFromShiftType(resolvedShift),
          type: resolvedDuration,
          identity: isEditing ? editingIdentity : undefined,
          _change: "upsert",
        });

        upsertDraftRow(setDraftNurseRegular, row);
        cancelEdit();
        return;
      }

      if (!normalizeServiceName(formService))
        return message.warning("Please select or type Service");

      const serviceName = normalizeServiceName(formService);
      const oneTimeDup = checkOneTimeDuplicate(serviceName);
      if (!oneTimeDup.valid) return message.warning(oneTimeDup.msg);

      const row = normalizeRowFromLocal({
        key: isEditing ? editingKey : uuid(),
        kind: "onetime",
        shiftType: "One Time",
        duration: "-",
        service: serviceName,
        fee: feeInt,
        discount: discountObj,
        notes: formNotes,
        serviceType: serviceName,
        identity: isEditing ? editingIdentity : undefined,
        _change: "upsert",
      });

      upsertDraftRow(setDraftNurseOneTime, row);
      cancelEdit();
      return;
    }

    if (activeSection === "Physiotherapist") {
      if (!normalizeServiceName(formService))
        return message.warning("Please select or type Service");
      const serviceName = normalizeServiceName(formService);
      const isAssessment = isSameService(serviceName, "Assessment");

      const row = normalizeRowFromLocal({
        key: isEditing ? editingKey : uuid(),
        kind: isAssessment ? "physio_assessment" : "physio",
        shiftType: isAssessment ? "Visit" : "Treatment",
        duration: "-",
        service: serviceName,
        fee: feeInt,
        discount: discountObj,
        notes: formNotes,
        identity: isEditing ? editingIdentity : undefined,
        _change: "upsert",
      });

      upsertDraftRow(setDraftPhysio, row);
      cancelEdit();
    }
  };

  const roleToAPI = (v) => (normalizeRole(v) || "Nurse").trim().toLowerCase();

  const handleSaveSection = async () => {
    if (readOnly) return;
    if (!isSupportedSection) return message.info(`${activeSection} pricing is coming soon`);
    if (!draftActiveData.length) return;

    setSaving(true);
    try {
      if (activeSection === "Nurse") {
        if (nurseMode === "regular") {
          const merged = applyDraft(savedNurseRegular, draftNurseRegular);
          const regular = merged.map((item) => ({
            shift: shiftFromShiftType(item.shiftType),
            type: item.duration,
            serviceName: normalizeServiceName(item.serviceName || ""),
            role: roleToAPI(item.role),
            services: uniqTrimmed(item.services),
            fee: clampInt(item.fee, { min: 0, max: MAX_FEE }) || 0,
            notes: item.notes || "",
            discount: item.discount
              ? { type: item.discount.type, value: Number(item.discount.value) }
              : undefined,
          }));

          const payload = {
            dutyType: "regularShifts",
            nurseService: { regularShifts: { slots: regular } },
          };

          const resp = await servicePricing(payload);
          if (resp?.error === false) {
            message.success("Saved successfully!");
            secureLocalStorage.setItem("pricingDone", true);
            await fetchPartnerData();
          } else {
            message.error(resp?.message || "Failed to save");
          }
          return;
        }

        const merged = applyDraft(savedNurseOneTime, draftNurseOneTime);
        const oneTime = merged.map((item) => {
          const obj = {
            serviceType: item.service,
            fee: clampInt(item.fee, { min: 0, max: MAX_FEE }) || 0,
            notes: item.notes || "",
            discount: item.discount
              ? { type: item.discount.type, value: Number(item.discount.value) }
              : undefined,
          };
          if (isGeneralService(item.service)) obj.services = safeArr(nurseGeneralServiceInfo);
          return obj;
        });

        const payload = {
          dutyType: "oneTimeShifts",
          nurseService: { oneTimeShifts: { services: oneTime } },
        };
        const resp = await servicePricing(payload);
        if (resp?.error === false) {
          message.success("Saved successfully!");
          secureLocalStorage.setItem("pricingDone", true);
          await fetchPartnerData();
        } else {
          message.error(resp?.message || "Failed to save");
        }
        return;
      }

      if (activeSection === "Physiotherapist") {
        const merged = applyDraft(savedPhysio, draftPhysio);
        const assessmentRow = merged.find((x) => isSameService(x.service, "Assessment"));
        const assessmentFees = assessmentRow
          ? clampInt(assessmentRow.fee, { min: 0, max: MAX_FEE }) || 0
          : 0;

        const services = merged
          .filter((x) => !isSameService(x.service, "Assessment"))
          .map((item) => ({
            name: item.service,
            fee: clampInt(item.fee, { min: 0, max: MAX_FEE }) || 0,
            notes: item.notes || "",
            discount: item.discount
              ? { type: item.discount.type, value: Number(item.discount.value) }
              : undefined,
          }))
          .filter((x) => x.name && x.fee > 0);

        const payload = {
          dutyType: "physioService",
          [PHYSIO_WRAPPER_KEY]: { assessmentFees, services },
        };
        const resp = await servicePricing(payload);
        if (resp?.error === false) {
          message.success("Saved successfully!");
          secureLocalStorage.setItem("pricingDone", true);
          await fetchPartnerData();
        } else {
          message.error(resp?.message || "Failed to save");
        }
      }
    } catch (e) {
      console.error(e);
      message.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const savedIdentitySet = useMemo(
    () => new Set(savedActiveData.map(identityOf)),
    [savedActiveData]
  );

  const baseCols = useMemo(() => {
    const cols = [
      {
        title: "Shift",
        dataIndex: "shiftType",
        key: "shiftType",
        width: 105,
        ellipsis: true,
        // Show combined label in table for regular rows
        render: (v, record) => {
          if (record?.kind === "regular" && record?.duration && record.duration !== "-") {
            const opt = SHIFT_DURATION_OPTIONS.find(
              (o) => o.shiftType === v && o.duration === record.duration
            );
            return opt ? opt.label : `${v} ${record.duration}`;
          }
          return v;
        },
      },
    ];

    // Duration column only for non-regular rows (regular has it merged into Shift)
    if (!(activeSection === "Nurse" && nurseMode === "regular")) {
      cols.push({
        title: "Duration",
        dataIndex: "duration",
        key: "duration",
        width: 80,
        ellipsis: true,
      });
    }

    if (activeSection === "Nurse" && nurseMode === "regular") {
      cols.push(
        {
          title: "Service Name",
          dataIndex: "serviceName",
          key: "serviceName",
          width: 150,
          ellipsis: true,
        },
        {
          title: "Role",
          dataIndex: "role",
          key: "role",
          width: 110,
          ellipsis: true,
          render: (v) => capitalizeFirstLetter(normalizeRole(v)),
        },
        {
          title: "Services",
          dataIndex: "services",
          key: "services",
          width: 180,
          ellipsis: true,
          render: (arr) => {
            const list = uniqTrimmed(arr);
            const hasGeneral = list.some((x) => isGeneralService(x));
            const txt = list.length ? list.join(", ") : "--";
            return (
              <div className="flex items-center gap-2 min-w-0">
                <span className="truncate" title={txt}>
                  {txt}
                </span>
                {hasGeneral && nurseGeneralServiceInfo.length > 0 && (
                  <Popover content={generalInfoContent} trigger="click" placement="right">
                    <button
                      type="button"
                      className="flex-none flex items-center justify-center w-5 h-5 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                    >
                      <Info className="w-3 h-3" />
                    </button>
                  </Popover>
                )}
              </div>
            );
          },
        }
      );
    } else {
      cols.push({
        title: "Service",
        dataIndex: "service",
        key: "service",
        width: 200,
        ellipsis: true,
        render: (v, record) => {
          const showGeneralInfoBtn =
            activeSection === "Nurse" &&
            nurseMode === "onetime" &&
            isGeneralService(record?.service) &&
            nurseGeneralServiceInfo.length > 0;
          return (
            <div className="flex items-center gap-2 min-w-0">
              <span className="truncate" title={v}>
                {v}
              </span>
              {showGeneralInfoBtn && (
                <Popover content={generalInfoContent} trigger="click" placement="right">
                  <button
                    type="button"
                    className="flex-none flex items-center justify-center w-5 h-5 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                  >
                    <Info className="w-3 h-3" />
                  </button>
                </Popover>
              )}
            </div>
          );
        },
      });
    }

    cols.push(
      {
        title: "Fee",
        dataIndex: "finalFee",
        key: "finalFee",
        width: 105,
        render: (_, record) => {
          if (record?._change === "delete")
            return <span className="text-gray-400">--</span>;
          const hasDiscount = record?.discount && Number(record.discount.value) > 0;
          return (
            <div className="leading-tight">
              <div className="font-medium text-gray-900">
                ₹{record.finalFee ?? record.fee}
              </div>
              {hasDiscount && (
                <div className="text-[10px] text-gray-400 line-through">
                  ₹{record.fee}
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: "Discount",
        dataIndex: "discount",
        key: "discount",
        width: 85,
        render: (d, record) =>
          record?._change === "delete" ? "--" : formatDiscount(normalizeDiscount(d)),
      },
      {
        title: "Notes",
        dataIndex: "notes",
        key: "notes",
        width: 140,
        ellipsis: true,
        render: (v) => (v ? <span title={v}>{v}</span> : "--"),
      }
    );

    return cols;
  }, [activeSection, nurseMode, nurseGeneralServiceInfo.length, generalInfoContent]);

  const savedColumns = useMemo(() => {
    if (readOnly) return baseCols;
    return [
      ...baseCols,
      {
        title: "Action",
        key: "action",
        width: 80,
        fixed: "right",
        render: (_, record) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-blue-500 hover:text-blue-700 disabled:opacity-50"
              onClick={() => startEditRow(record)}
              disabled={saving}
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              type="button"
              className="text-red-500 hover:text-red-700 disabled:opacity-50"
              onClick={() => queueDeleteSaved(record)}
              disabled={saving}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ];
  }, [readOnly, baseCols, saving]);

  const draftColumns = useMemo(() => {
    if (readOnly) return baseCols;
    return [
      {
        title: "Status",
        key: "status",
        width: 90,
        render: (_, record) => {
          if (record?._change === "delete")
            return <span className="text-red-600 font-medium text-xs">Remove</span>;
          const existsInSaved = savedIdentitySet.has(identityOf(record));
          return (
            <span
              className={`font-medium text-xs ${existsInSaved ? "text-amber-600" : "text-green-600"
                }`}
            >
              {existsInSaved ? "Update" : "New"}
            </span>
          );
        },
      },
      ...baseCols,
      {
        title: "Act",
        key: "action",
        width: 80,
        fixed: "right",
        render: (_, record) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-blue-500 hover:text-blue-700 disabled:opacity-50"
              onClick={() => startEditRow(record)}
              disabled={saving || record?._change === "delete"}
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              type="button"
              className="text-red-500 hover:text-red-700 disabled:opacity-50"
              onClick={() => removeDraftRow(record)}
              disabled={saving}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ];
  }, [readOnly, baseCols, saving, savedIdentitySet]);

  const regularServicesOptions = useMemo(() => {
    const all = uniqTrimmed([
      // "General Services",
      ...safeArr(nurseGeneralServiceInfo),
      ...safeArr(nurseIndividualServices),
    ]);
    return all.map((s) => ({ label: s, value: s }));
  }, [nurseGeneralServiceInfo, nurseIndividualServices]);

  const tableScrollX = useMemo(() => {
    if (activeSection === "Nurse" && nurseMode === "regular") return 1080;
    return 900;
  }, [activeSection, nurseMode]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) return <Loader loading={loading} />;

  return (
    <div
      className="pricing-shell w-full bg-[#F4F6FB] px-3 py-3 overflow-hidden"
      style={{ height: "calc(var(--app-vh, 1vh) * 92)", fontFamily: "'Poppins', sans-serif" }}
    >
      <style>{`
        @supports (height: 100dvh) { .pricing-shell { height: 100dvh; } }
        @supports (height: 100svh) { .pricing-shell { height: 100svh; } }
        .pricing-shell * { box-sizing: border-box; }
        .pricing-shell .ant-input,
        .pricing-shell .ant-input-number,
        .pricing-shell .ant-select-selector {
          border-radius: 8px !important;
          height: 38px !important;
        }
        .pricing-shell .ant-input-number-input,
        .pricing-shell .ant-select-selection-item,
        .pricing-shell .ant-select-selection-placeholder,
        .pricing-shell .ant-select-selection-search-input,
        .pricing-shell .ant-select-item,
        .pricing-shell .ant-select-item-option-content,
        .pricing-shell .ant-picker-input > input {
          font-family: "Poppins", sans-serif !important;
        }
        .pricing-shell .ant-table { border-radius: 12px !important; }
        .pricing-scroll { -webkit-overflow-scrolling: touch; overscroll-behavior: contain; }
      `}</style>

      <div className="bg-white h-full rounded-xl shadow-sm border border-[#E1E7FF] flex flex-col overflow-hidden">
        <div className="pricing-scroll flex-1 min-h-0 overflow-y-auto">

          {/* Top tabs */}
          <div className="flex items-center justify-center mb-1 pt-4">
            <div className="w-full max-w-[520px] min-w-0 px-4">
              {serviceTabs.length > 1 ? (
                <ToggleTabs
                  options={serviceTabs}
                  active={activeSection}
                  setActive={setActiveSection}
                />
              ) : (
                <div className="text-sm font-semibold text-gray-800">
                  {serviceTabs[0]?.label || activeSection}
                </div>
              )}
            </div>
          </div>

          {/* Nurse sub-tabs */}
          {isSupportedSection && isNurse && !readOnly && (
            <div className="flex mb-4 px-4">
              <div className="relative w-full max-w-[350px]">
                <div className="flex border-b border-gray-200">
                  <button
                    type="button"
                    className={`flex-1 text-center pb-3 pt-1 font-medium transition-colors
                      ${nurseMode === "onetime" ? "text-blue-600" : "text-gray-500 hover:text-blue-500"}`}
                    onClick={() => { setNurseMode("onetime"); cancelEdit(); }}
                  >
                    One-Time Service
                  </button>
                  <button
                    type="button"
                    className={`flex-1 text-center pb-3 pt-1 font-medium transition-colors
                      ${nurseMode === "regular" ? "text-blue-600" : "text-gray-500 hover:text-blue-500"}`}
                    onClick={() => { setNurseMode("regular"); cancelEdit(); }}
                  >
                    Regular Service
                  </button>
                </div>
                <div className="absolute bottom-0 left-0 flex w-full">
                  <span
                    className="block h-[2.5px] rounded bg-blue-600 transition-all duration-300"
                    style={{
                      position: "absolute",
                      width: "50%",
                      transition: "left 0.3s cubic-bezier(.4,0,.2,1)",
                      left: nurseMode === "onetime" ? "0%" : "50%",
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <h3 className="text-[15px] font-semibold text-[#2263E6] mb-2 px-4">
            {isNurse ? "Nursing Details" : `${activeSection} Details`}
          </h3>

          {/* Content */}
          <div className="w-full px-4 pb-4">
            {readOnly ? (
              <div className="w-full max-w-full overflow-x-auto">
                <Table
                  columns={baseCols}
                  dataSource={savedActiveData}
                  pagination={false}
                  rowKey="key"
                  scroll={{ x: tableScrollX }}
                  tableLayout="fixed"
                  size="small"
                  locale={{ emptyText: `No ${activeSection} pricing available` }}
                />
              </div>
            ) : !isSupportedSection ? (
              <div className="border border-dashed border-slate-300 bg-white rounded-lg p-6">
                <div className="text-base font-semibold text-gray-900 mb-1">
                  {activeSection} — Coming soon
                </div>
                <div className="text-sm text-gray-600">
                  Pricing setup for{" "}
                  <span className="font-medium">{activeSection}</span> will be available soon.
                </div>
              </div>
            ) : (
              <>
                {/* ── FORM CARD ─────────────────────────────────────────── */}
                <div className="border border-[#E5E9F5] bg-white rounded-xl px-5 pt-4 pb-5 mb-4">

                  {/* ONE-TIME NURSE */}
                  {isNurse && nurseMode === "onetime" && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end w-full mb-3">
                        <div className={`${fieldWrap} md:col-span-6`}>
                          <div className="mb-1">{renderServiceLabelWithInfo()}</div>
                          {renderServiceInput()}
                        </div>

                        <div className={`${fieldWrap} md:col-span-2 mt-2`}>
                          <label className={labelCls}>Fee (in ₹)</label>
                          <InputNumber
                            placeholder="0"
                            value={formFee}
                            onChange={(v) => setFormFee(clampInt(v, { min: 0, max: MAX_FEE }))}
                            min={0}
                            max={MAX_FEE}
                            precision={0}
                            step={1}
                            className="w-full p-2"
                            controls={false}
                            size="small"
                            style={{ borderRadius: 8, height: 38 }}
                            parser={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                            formatter={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                          />
                        </div>

                        <div className="md:col-span-3 min-w-0 mt-2">
                          {renderDiscountField()}
                        </div>

                        <div className="md:col-span-1 flex justify-end items-end">
                          {renderActionButtons()}
                        </div>
                      </div>

                      <div className="mt-1 w-full flex items-end gap-3">
                        <div className="flex-1">
                          <label className={labelCls}>Notes</label>
                          <Input
                            placeholder="Notes"
                            value={formNotes}
                            onChange={(e) => setFormNotes(e.target.value)}
                            size="small"
                            style={{ borderRadius: 8, height: 40 }}
                            className="font-poppins"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* REGULAR NURSE */}
                  {isNurse && nurseMode === "regular" && (
                    <>
                      {/* Row 1: Service Name | Role | Shift & Duration (merged) */}
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                        <div className={`${fieldWrap} md:col-span-5`}>
                          <label className={labelCls}>Service Name</label>
                          <Input
                            placeholder="Enter service name"
                            value={formServiceName}
                            onChange={(e) => setFormServiceName(e.target.value)}
                            size="small"
                            style={{ borderRadius: 8, height: 38 }}
                            className="font-poppins text-start"
                          />
                        </div>

                        <div className={`${fieldWrap} md:col-span-3`}>
                          <label className={labelCls}>Role</label>
                          <Select
                            value={formRole || undefined}
                            onChange={setFormRole}
                            size="small"
                            style={{ borderRadius: 8, height: 38 }}
                            placeholder="Select role"
                          >
                            {NURSE_ROLES.map((r) => (
                              <Select.Option key={r.value} value={r.value}>
                                {r.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>

                        {/* ── MERGED Shift & Duration dropdown ── */}
                        <div className={`${fieldWrap} md:col-span-4`}>
                          <label className={labelCls}>Shift & Duration</label>
                          <Select
                            value={formShiftDuration || undefined}
                            onChange={(val) => {
                              setFormShiftDuration(val);
                              const { shiftType, duration } = decodeShiftDuration(val);
                              setFormShiftType(shiftType);
                              setFormDuration(duration);
                            }}
                            size="small"
                            style={{ borderRadius: 8, height: 38 }}
                            placeholder="Select type"
                            disabled={Boolean(editingKey)}
                          >
                            {SHIFT_DURATION_OPTIONS.map((opt) => (
                              <Select.Option key={opt.value} value={opt.value}>
                                {opt.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>
                      </div>

                      {/* Row 2: Services */}
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                        <div className={`${fieldWrap} md:col-span-12`}>
                          <label className={labelCls}>Services</label>
                          <Select
                            mode="tags"
                            placeholder="Select services"
                            value={formServices}
                            onChange={(vals) => setFormServices(uniqTrimmed(vals))}
                            options={regularServicesOptions}
                            size="small"
                            tokenSeparators={[","]}
                            maxTagCount="responsive"
                            style={{ borderRadius: 8, minHeight: 38 }}
                          />
                        </div>
                      </div>

                      {/* Row 3: Fee | Discount */}
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                        <div className={`${fieldWrap} md:col-span-3`}>
                          <label className={labelCls}>Fee (in ₹)</label>
                          <InputNumber
                            placeholder="0"
                            value={formFee}
                            onChange={(v) => setFormFee(clampInt(v, { min: 0, max: MAX_FEE }))}
                            min={0}
                            max={MAX_FEE}
                            precision={0}
                            step={1}
                            controls={false}
                            size="small"
                            className="w-full p-2"
                            style={{ borderRadius: 8, height: 38 }}
                            parser={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                            formatter={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                          />
                        </div>

                        <div className={`${fieldWrap} md:col-span-4`}>
                          {renderDiscountField()}
                        </div>
                      </div>

                      {/* Row 4: Notes + Add */}
                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <label className={labelCls}>Notes</label>
                          <Input
                            placeholder="Notes"
                            value={formNotes}
                            onChange={(e) => setFormNotes(e.target.value)}
                            size="small"
                            style={{ borderRadius: 8, height: 40 }}
                            className="font-poppins"
                          />
                        </div>
                        <div className="flex-none pb-1">{renderActionButtons()}</div>
                      </div>
                    </>
                  )}

                  {/* Physio form */}
                  {activeSection === "Physiotherapist" && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                        <div className={`${fieldWrap} md:col-span-6`}>
                          <label className={labelCls}>Service</label>
                          {renderServiceInput()}
                        </div>

                        <div className={`${fieldWrap} md:col-span-3`}>
                          <label className={labelCls}>Fee (in ₹)</label>
                          <InputNumber
                            placeholder="0"
                            value={formFee}
                            onChange={(v) => setFormFee(clampInt(v, { min: 0, max: MAX_FEE }))}
                            min={0}
                            max={MAX_FEE}
                            precision={0}
                            step={1}
                            className="w-full text-center"
                            controls={false}
                            size="small"
                            style={{ borderRadius: 8, height: 38 }}
                            parser={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                            formatter={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                          />
                        </div>

                        <div className="md:col-span-3">{renderDiscountField()}</div>
                      </div>

                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <label className={labelCls}>Notes</label>
                          <Input
                            placeholder="Notes"
                            value={formNotes}
                            onChange={(e) => setFormNotes(e.target.value)}
                            size="small"
                            style={{ borderRadius: 8, height: 40 }}
                            className="font-poppins"
                          />
                        </div>
                        <div className="flex-none pb-1">{renderActionButtons()}</div>
                      </div>
                    </>
                  )}
                </div>

                {/* ── PENDING TABLE ──────────────────────────────────────── */}
                <div className="flex items-center justify-between mb-2 mt-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Pending Changes</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      New / updated / removed items will appear here.
                    </div>
                  </div>
                  {hasDraftChanges && (
                    <button
                      className="px-4 py-1.5 bg-blue-600 text-white rounded-sm text-xs font-semibold hover:bg-blue-700 disabled:opacity-60"
                      onClick={handleSaveSection}
                      disabled={saving}
                      type="button"
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                  )}
                </div>

                {hasDraftChanges ? (
                  <div className="w-full max-w-full overflow-x-auto mb-4">
                    <Table
                      columns={draftColumns}
                      dataSource={draftActiveData}
                      pagination={false}
                      rowKey="key"
                      scroll={{ x: tableScrollX }}
                      tableLayout="fixed"
                      locale={{ emptyText: "No pending changes" }}
                      size="small"
                      className="rounded-xl overflow-hidden border border-[#E5E9F5]"
                    />
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 bg-white border border-[#E5E9F5] rounded-lg p-3 mb-4">
                    No pending changes. (Everything is already saved.)
                  </div>
                )}

                {/* ── SAVED TABLE ────────────────────────────────────────── */}
                <div className="mb-2 mt-2">
                  <div className="text-sm font-semibold text-gray-900">Saved Pricing</div>
                </div>

                <div className="w-full max-w-full overflow-x-auto mb-2">
                  <Table
                    columns={savedColumns}
                    dataSource={savedActiveData}
                    pagination={false}
                    rowKey="key"
                    scroll={{ x: tableScrollX }}
                    tableLayout="fixed"
                    locale={{ emptyText: "No saved pricing" }}
                    size="small"
                    className="rounded-xl overflow-hidden border border-[#E5E9F5]"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        {!readOnly && (
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-4 py-3 flex-none">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-8 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium"
            >
              Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pricing;